import { forwardRef, MouseEventHandler, ReactElement, RefObject } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { Spinner } from "../Spinner/Spinner";
import { ButtonColor, ButtonSize, ButtonStatus, FontSize } from "./types";

interface ButtonProps {
	title: string;
	onClick?: MouseEventHandler;
	type?: "submit" | "reset" | "button";
	disabled?: boolean;
	disabledInGray?: boolean;
	status?: ButtonStatus;
	color?: ButtonColor;
	size?: ButtonSize;
	fontSize?: FontSize;
	isLoading?: boolean;
	image?: ReactElement;
	className?: string;
	textClassName?: string;
	testId?: string;
	iconRight?: boolean;
	ref?: RefObject<HTMLButtonElement>;
	subtitle?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			title,
			onClick,
			type = "submit",
			status,
			disabled,
			disabledInGray,
			isLoading,
			color = ButtonColor.BRAND,
			fontSize = FontSize.SM,
			size = ButtonSize.M,
			image,
			className,
			textClassName,
			testId,
			iconRight,
			subtitle = "",
		},
		ref
	) => {
		const { t } = useTranslation();
		const getStatusClasses = () => {
			switch (status) {
				case ButtonStatus.SUCCESS:
					return "!border-brand-light !bg-transparent !text-brand-light";
				case ButtonStatus.ACTION:
					return "!border-blue-action !bg-white !text-blue-action";
			}
		};

		const getColorClasses = () => {
			switch (color) {
				case ButtonColor.BRAND:
					return "bg-blue-action hover:bg-blue-action-dark hover:shadow-md text-white border-transparent";
				case ButtonColor.ACTION:
					return "bg-brand-light hover:shadow-md text-white border-transparent";
				case ButtonColor.ACTION_SOFT:
					return "bg-light-blue hover:bg-blue-action-200 text-brand-light border-transparent";
				case ButtonColor.ACTION_SECONDARY:
					return classNames(
						"bg-white hover:shadow-md border border-gray-200 text-text",
						disabledInGray ? "disabled:!border-gray-300 disabled:!bg-transparent" : ""
					);
				case ButtonColor.DANGER:
				case ButtonColor.ERROR:
					return "bg-status-error hover:shadow-md border-status-error text-white";
				case ButtonColor.DANGER_SECONDARY:
					return "bg-white border-status-error hover:shadow-md text-status-error";
				case ButtonColor.WARNING:
					return "bg-status-warning hover:shadow-md text-white border-transparent";
				case ButtonColor.SUCCESS:
					return "bg-status-success hover:shadow-md text-white";
				case ButtonColor.DISABLED:
					return "bg-status-disabled hover:shadow-md text-gray-500 border-transparent";
			}
		};

		const getSizeClasses = () => {
			switch (size) {
				case ButtonSize.XS:
					return "h-[28px] px-2 text-xs rounded-lg";
				case ButtonSize.S:
					return "h-[32px] px-4 text-xs";
				case ButtonSize.M:
					return "h-[38px] px-4";
				case ButtonSize.ML:
					return "h-[42px] px-4";
				case ButtonSize.L:
					return "h-[48px] px-6";
			}
		};

		const getFontSizeClasses = () => {
			switch (fontSize) {
				case FontSize.XS:
					return "text-xs";
				case FontSize.SM:
					return "text-sm";
				default:
					return "";
			}
		};

		return (
			<button
				ref={ref}
				className={classNames(
					"appearance-none cursor-pointer text-center font-semibold rounded-[10px] w-fit border duration-200",
					getColorClasses(),
					status ? getStatusClasses() : "",
					getSizeClasses(),
					getFontSizeClasses(),
					disabled ? "opacity-60" : "",
					disabledInGray ? "disabled:bg-gray-300 disabled:text-gray-500" : "",
					className || ""
				)}
				data-clicktarget="tableaction"
				data-test={testId}
				disabled={isLoading || disabled}
				type={type}
				onClick={onClick}
			>
				{isLoading ? (
					<div className="flex items-center justify-center">
						{[ButtonColor.BRAND, ButtonColor.ACTION].includes(color) ? (
							<Spinner />
						) : (
							<Spinner color="#4d66f5" />
						)}
						<div className="ml-3 whitespace-nowrap">{t("basics.wait")}</div>
					</div>
				) : (
					<div
						className={classNames(
							"flex items-center justify-center max-w-full overflow-hidden text-center whitespace-nowrap text-ellipsis",
							textClassName || ""
						)}
					>
						{iconRight ? (
							<>
								{title}
								{image}
							</>
						) : subtitle.length > 0 ? (
							<>
								<div className="flex flex-row items-center ">
									{image}
									<div className="flex flex-col">
										<p className="text-sm max-h-[50%]">{title}</p>
										<p className="text-xs font-medium max-h-[40%] text-gray-900">{subtitle}</p>
									</div>
								</div>
							</>
						) : (
							<>
								{image}
								{title}
							</>
						)}
					</div>
				)}
			</button>
		);
	}
);

Button.displayName = "Button";
